/**
 * Cost Inbox → Email-in: the forwarding address, who may forward, and a log of every
 * email Resend handed us (added / rejected / no attachment).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Mail, Loader2, Plus, X, RefreshCw, ExternalLink, Copy, CheckCircle2, AlertTriangle, Paperclip, Pencil } from 'lucide-react';

const fmtDT = (s) => (s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const TONE = { created: 'text-emerald-300', created_po: 'text-emerald-300', processing: 'text-gray-400', rejected_sender: 'text-red-300', wrong_address: 'text-amber-300', auth_failed: 'text-red-300', no_attachment: 'text-amber-300', error: 'text-red-300', disabled: 'text-gray-400' };

function AllowedSenders({ settings, onSave }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const list = settings.allowed_senders || [];
  const save = async (next) => {
    setBusy(true);
    try { await onSave({ allowed_senders: next }); setDraft(''); }
    finally { setBusy(false); }
  };
  const add = () => {
    const m = draft.match(/<([^<>\s]+@[^<>\s]+)>/);
    const a = (m ? m[1] : draft).trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a)) { toast.error('Enter a full email address'); return; }
    if (list.includes(a)) { toast.info('Already allowed'); return; }
    save([...list, a]);
  };
  return (
    <div className="bg-[#2A3240] border border-white/10 rounded-lg p-4" data-testid="emailin-senders">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="font-medium">Who may forward invoices</div>
          <div className="text-xs text-gray-400">Emails from anyone else are dropped and logged below.</div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-300">
          Email-in {settings.enabled ? 'on' : 'off'}
          <Switch checked={!!settings.enabled} onCheckedChange={(v) => onSave({ enabled: v })} className="data-[state=unchecked]:bg-gray-500" data-testid="emailin-enabled" />
        </label>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {list.map(a => (
          <span key={a} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-gray-200" data-testid={`emailin-sender-${a}`}>
            {a}
            <button type="button" onClick={() => save(list.filter(x => x !== a))} disabled={busy} className="text-gray-500 hover:text-red-300" title="Remove" data-testid={`emailin-sender-remove-${a}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        {list.length === 0 && <span className="text-xs text-amber-300">Nobody is allowed yet — add an address.</span>}
      </div>
      <div className="flex items-center gap-2">
        <Input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder="name@lux-developments.co.uk" className="bg-[#1E2530] border-white/15 text-white text-sm max-w-sm" data-testid="emailin-sender-input" />
        <Button size="sm" onClick={add} disabled={busy || !draft.trim()} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a]" data-testid="emailin-sender-add">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Allow</>}
        </Button>
      </div>
    </div>
  );
}

function AddressCard({ settings, webhookConfigured, onSave }) {
  return (
    <div className="bg-[#2A3240] border border-white/10 rounded-lg p-4 space-y-4" data-testid="emailin-address-card">
      <EditableAddress label="Forward supplier invoices to" field="address" value={settings.address} placeholder="inbox@luxdev.app" onSave={onSave} testPrefix="emailin-address"
        hint="Each email becomes one Cost Inbox item: a PDF is used as-is; photos (JPG/PNG/HEIC) and Word documents are converted to PDF — signature and logo images are ignored; only when nothing usable is attached is the email text itself captured as the invoice." />
      <EditableAddress label="Email client purchase orders to" field="po_address" value={settings.po_address} placeholder="po@luxdev.app" onSave={onSave} testPrefix="emailin-po-address"
        hint="Each email becomes one Client PO — the AI reads the order and suggests the project from the site address." />
      <label className="inline-flex items-center gap-2 text-xs text-gray-300">
        <Switch checked={settings.body_fallback !== false} onCheckedChange={(v) => onSave({ body_fallback: v })} className="data-[state=unchecked]:bg-gray-500" data-testid="emailin-body-fallback" />
        Capture the email text as the invoice when there's no attachment
      </label>
      <div className={`inline-flex items-center gap-1.5 text-xs ${webhookConfigured ? 'text-emerald-300' : 'text-amber-300'}`} data-testid="emailin-webhook-state">
        {webhookConfigured ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
        {webhookConfigured ? 'Resend webhook connected' : 'Waiting for the Resend webhook secret (setup guide, part C) — nothing arrives until then'}
      </div>
    </div>
  );
}

function EditableAddress({ label, field, value, placeholder, onSave, testPrefix, hint }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value || ''); toast.success('Address copied'); }
    catch { toast.error('Copy blocked by the browser — select the address and copy it'); }
  };
  const save = async () => {
    const a = draft.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a)) { toast.error(`Enter a full email address, e.g. ${placeholder}`); return; }
    setBusy(true);
    try { await onSave({ [field]: a }); setEditing(false); }
    catch { /* toast shown by caller */ }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      {editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()} placeholder={placeholder} className="bg-[#1E2530] border-white/15 text-white text-sm max-w-xs" autoFocus data-testid={`${testPrefix}-input`} />
          <Button size="sm" onClick={save} disabled={busy} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a]" data-testid={`${testPrefix}-save`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(value || ''); }} className="text-gray-300 hover:text-white" data-testid={`${testPrefix}-cancel`}>Cancel</Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-lg text-[#F5A623] font-semibold" data-testid={testPrefix}>{value || 'not configured'}</code>
          {value && <Button variant="ghost" size="sm" onClick={copy} className="text-gray-300 hover:text-white h-7 px-2" data-testid={`${testPrefix}-copy`}><Copy className="w-3.5 h-3.5" /></Button>}
          <Button variant="ghost" size="sm" onClick={() => { setDraft(value || ''); setEditing(true); }} className="text-gray-300 hover:text-white h-7 px-2" title="Change the address (must be on a domain Resend receives for)" data-testid={`${testPrefix}-edit`}><Pencil className="w-3.5 h-3.5" /></Button>
        </div>
      )}
      <div className="text-xs text-gray-400 mt-1.5">{hint}</div>
    </div>
  );
}

export default function CostInboxEmailIn() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { if (user && !isAdmin) navigate('/projects'); }, [user, isAdmin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/cost-inbox/email-in'); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load email-in'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    try {
      const r = await api.put('/cost-inbox/email-in/settings', patch);
      setData(d => ({ ...d, settings: r.data }));
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); throw e; }
  };

  const [reprocessing, setReprocessing] = useState(null);
  const reprocess = async (r) => {
    if (!window.confirm(`Run "${r.subject || '(no subject)'}" through the attachment rules again?${r.item_id ? ' The unposted inbox item made from it will be moved to Deleted and replaced.' : ''}`)) return;
    setReprocessing(r.email_id);
    try {
      const res = await api.post(`/cost-inbox/email-in/${r.email_id}/reprocess`);
      toast.success(res.data.status === 'created' ? `Re-processed — ${res.data.reason || 'added to the inbox'}` : `${res.data.status_label || res.data.status}: ${res.data.reason || ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not re-process'); }
    finally { setReprocessing(null); }
  };

  return (
    <div className="min-h-screen bg-[#1E2530] text-white p-6" data-testid="cost-inbox-emailin-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <button type="button" onClick={() => navigate('/cost-inbox')} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 mb-2" data-testid="emailin-back">
              <ArrowLeft className="w-3.5 h-3.5" /> Cost Inbox
            </button>
            <h1 className="text-3xl font-bold flex items-center gap-2"><Mail className="w-7 h-7 text-[#F5A623]" /> Email-in</h1>
            <p className="text-sm text-gray-400 mt-1">Forward supplier invoices by email and they land in the Cost Inbox.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="emailin-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>

        {loading || !data ? (
          <div className="p-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <AddressCard settings={data.settings} webhookConfigured={data.webhook_configured} onSave={saveSettings} />
              <AllowedSenders settings={data.settings} onSave={saveSettings} />
            </div>

            <div className="bg-[#2A3240] border border-white/10 rounded-lg overflow-x-auto">
              <div className="px-4 py-3 border-b border-white/10 font-medium">Recent emails</div>
              {data.log.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm" data-testid="emailin-log-empty">Nothing received yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="emailin-log">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10 [&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
                      <th>Received</th><th>From</th><th>Subject</th><th>Attachments</th><th>Outcome</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.log.map(r => (
                      <tr key={r.email_id} className="border-b border-white/5 hover:bg-white/5 [&>td]:px-3 [&>td]:py-2 align-top" data-testid={`emailin-log-${r.email_id}`}>
                        <td className="text-xs text-gray-400 whitespace-nowrap">{fmtDT(r.received_at)}</td>
                        <td className="text-xs text-gray-200">{r.from_name ? <div>{r.from_name}</div> : null}<div className="text-gray-400">{r.from}</div></td>
                        <td className="text-xs text-gray-200 max-w-[260px] truncate" title={r.subject}>{r.subject || '(no subject)'}</td>
                        <td className="text-xs text-gray-400">
                          {(r.attachments || []).length === 0 ? '—' : (r.attachments || []).map((a, i) => (
                            <div key={i} className={`inline-flex items-center gap-1 mr-2 ${a.used ? 'text-gray-200' : 'text-gray-500 line-through decoration-gray-600'}`} title={a.used ? `Used${a.bytes ? ` · ${Math.round(a.bytes / 1024)} KB` : ''}` : (a.reason || 'Not used')} data-testid={`emailin-att-${a.used ? 'used' : 'skipped'}`}>
                              <Paperclip className="w-3 h-3" />{a.filename}
                            </div>
                          ))}
                        </td>
                        <td>
                          <div className={`text-xs ${TONE[r.status] || 'text-gray-300'}`}>{r.status_label}</div>
                          {r.reason && <div className="text-[10px] text-gray-500 max-w-[320px]">{r.reason}</div>}
                        </td>
                        <td>
                          {r.item_id && (
                            <button type="button" onClick={() => navigate(`/cost-inbox?open=${r.item_id}`)} className="text-xs text-[#F5A623] hover:underline inline-flex items-center gap-1" data-testid={`emailin-open-${r.email_id}`}>
                              open <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                          {r.po_id && (
                            <button type="button" onClick={() => navigate(`/client-pos?open=${r.po_id}`)} className="text-xs text-[#F5A623] hover:underline inline-flex items-center gap-1" data-testid={`emailin-open-po-${r.email_id}`}>
                              open PO <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                          {r.kind !== 'po' && !r.po_id && ['created', 'no_attachment', 'error'].includes(r.status) && (
                            <button type="button" onClick={() => reprocess(r)} disabled={reprocessing === r.email_id} title="Run this email through the attachment rules again" className="block mt-1 text-xs text-gray-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid={`emailin-reprocess-${r.email_id}`}>
                              {reprocessing === r.email_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} re-process
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
